import React, { useCallback, useMemo } from "react";
import TextbookLiveChapterPageFlow from "./TextbookLiveChapterPageFlow";
import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";
import { NovelStorySceneReadOnly } from "./NovelStorySceneManuscript";

function clean(value) {
  return String(value ?? "").trim();
}
function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

/*
 * Reuse Textbook's browser-measured multi-column page projection in proofMode.
 * No server/Canon data is modified. The measured chapter-page count is NOT
 * the final press-ready PDF page count, which needs separate export proofing.
 */
export default function NovelStoryPaginatedInterior({
  entry,
  sceneById,
  bookFormat = {},
  isFR = false,
  onBrowserPageMetrics,
}) {
  const item = useMemo(() => entry?.item || {}, [entry?.item]);
  const kind = entry?.kind;
  const title = clean(item.title) || (isFR ? "Sans titre" : "Untitled");
  const chapterPrefix = isFR ? "Chapitre" : "Chapter";
  const chapterHeading = new RegExp(`^${chapterPrefix}\\s+\\d+`, "i").test(title)
    ? title
    : `${chapterPrefix} ${(entry?.index ?? 0) + 1} · ${title}`;
  const matterHeading = /^(title|subtitle|titre|sous-titre)$/i.test(title) ? "" : title;
  const sceneIds = useMemo(() => safeArray(item.sceneIds), [item.sceneIds]);
  const blocks = useMemo(
    () => kind === "chapter"
      ? sceneIds.map((sceneId, sceneIndex) => ({
          blockId: `novel-proof-scene-${sceneId}-${sceneIndex}`,
          type: "novel_scene",
          sceneId,
          sceneNumber: sceneIndex + 1,
        }))
      : [{
          blockId: `novel-proof-matter-${kind}-${item.id || entry?.index || 0}`,
          type: "novel_matter",
          matter: item,
        }],
    [kind, sceneIds, item, entry?.index]
  );

  const sourceUnitKey = [kind, clean(item.id)].join(":");
  const handleWorkingPageMetrics = useCallback((metrics) => {
    // Receives actual browser DOM column measurement, not a physical PDF count.
    if (typeof onBrowserPageMetrics === "function") {
      onBrowserPageMetrics(sourceUnitKey, metrics);
    }
  }, [onBrowserPageMetrics, sourceUnitKey]);

  const renderBlock = (block) => {
    if (block.type === "novel_scene") {
      const scene = sceneById?.get(block.sceneId);
      return scene ? (
        <section
          data-novel-proof-paginated-scene={block.sceneId}
          style={{ margin: 0, breakInside: "auto" }}
        >
          <NovelStorySceneReadOnly scene={scene} lang={isFR ? "fr" : "en"} />
        </section>
      ) : (
        <p role="alert" data-novel-proof-paginated-missing-scene="true">
          {isFR ? "Scène enregistrée introuvable" : "Saved Canon Scene unavailable"}
          {" · "}{block.sceneNumber}
        </p>
      );
    }
    if (block.type === "novel_matter") {
      return (
        <ChildrensBookManuscriptReadOnly
          content={block.matter?.manuscriptContent || null}
          fallbackText={String(block.matter?.text || "")}
          lang={isFR ? "fr" : "en"}
          presentation="publication_proof"
        />
      );
    }
    return null;
  };

  return (
    <div data-novel-proof-paginated-interior={kind}>
      <style>{`
        /* Novel proof only: hide Textbook's editorial heading above its page controls.
           Keep all page controls and content flow intact. */
        [data-novel-proof-paginated-interior]
        [data-textbook-live-page-flow="true"] > div:first-of-type {
          display: none;
        }
        /* Hide the inherited “Chapter page” label for front/back matter as well.
           Keep the working page selector and Previous/Next enabled. */
        [data-novel-proof-paginated-interior]
        [data-textbook-live-page-status="true"] > span:first-child {
          display: none;
        }
      `}</style>
      <TextbookLiveChapterPageFlow
        key={`${kind}-${item.id || entry?.index || 0}`}
        chapterTitle={kind === "chapter" ? chapterHeading : matterHeading}
        blocks={blocks}
        sections={[]}
        sectionLayout={[]}
        formatPlan={bookFormat}
        isFR={isFR}
        canEdit={false}
        editMode={false}
        renderBlock={renderBlock}
        proofMode={true}
        stickyNavigation={false}
        onWorkingPageMetricsChange={handleWorkingPageMetrics}
      />
    </div>
  );
}
